import React, { useState, useEffect, useCallback } from 'react';
import DemographicVerificationPanel from './DemographicVerificationPanel';
import OnBehalfOfEntryForm from './OnBehalfOfEntryForm';
import { listSubmissions } from '../lib/demographic';

/**
 * DemographicVerificationQueue
 *
 * Lists submissions in pending_verification status, tenant-scoped by
 * the backend (GET /demographics already filters to actor.tenant_id
 * server-side — no client-side tenant filtering needed or trusted here).
 *
 * Tenant-WIDE clinician visibility is the approved staging-only scope
 * (design addendum, Section 4) — every pending submission in the tenant
 * shows here regardless of which clinician eventually reviews it, since
 * no assignment model exists yet. This is intentionally not narrowed
 * client-side to "my patients" — there's no data to narrow it by.
 */
export default function DemographicVerificationQueue() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showOnBehalfOfForm, setShowOnBehalfOfForm] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSubmissions({ status: 'pending_verification' });
      setSubmissions(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const handleResolved = () => {
    setSelectedId(null);
    setShowOnBehalfOfForm(false);
    loadQueue();
  };

  if (selectedId) {
    return (
      <DemographicVerificationPanel
        submissionId={selectedId}
        submission={submissions.find((s) => s.id === selectedId)}
        onBack={() => setSelectedId(null)}
        onResolved={handleResolved}
      />
    );
  }

  if (showOnBehalfOfForm) {
    return (
      <OnBehalfOfEntryForm
        onBack={() => setShowOnBehalfOfForm(false)}
        onResolved={handleResolved}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">Demographic Verification Queue</h3>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowOnBehalfOfForm(true)}
            className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-lg transition"
          >
            + Enter demographics for patient
          </button>
          <button
            onClick={loadQueue}
            className="text-xs text-slate-400 hover:text-white uppercase tracking-wide"
          >
            Refresh
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading queue…</p>}
      {error && <p className="text-sm text-red-400">{error}</p>}

      {!loading && !error && submissions.length === 0 && (
        <p className="text-sm text-slate-500">No submissions awaiting verification.</p>
      )}

      <div className="divide-y divide-slate-800 rounded-2xl border border-slate-800 overflow-hidden">
        {submissions.map((s) => (
          <button
            key={s.id}
            onClick={() => setSelectedId(s.id)}
            className="w-full text-left px-4 py-3 bg-slate-900/50 hover:bg-slate-800 transition flex items-center justify-between"
          >
            <div>
              <p className="text-sm font-semibold text-white">
                {s.first_name} {s.last_name}
              </p>
              <p className="text-xs text-slate-500">
                Submitted {new Date(s.submitted_at).toLocaleString()}
              </p>
            </div>
            <span className="text-xs font-black uppercase tracking-widest text-amber-400">
              Pending
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}